import React, { useState, useCallback } from 'react'
import { makeStyles } from '@material-ui/core/styles'
import { useDebouncedCallback } from 'use-debounce'
import Box from '@material-ui/core/Box'
import Grid from '@material-ui/core/Grid'
import List from '@material-ui/core/List'
import ListItemSecondaryAction from '@material-ui/core/ListItemSecondaryAction'
import ListItem from '@material-ui/core/ListItem'
import ListItemIcon from '@material-ui/core/ListItemIcon'
import ListItemText from '@material-ui/core/ListItemText'
import DragHandleIcon from '@material-ui/icons/DragHandle'
import Dialog from '@material-ui/core/Dialog'
import DialogContent from '@material-ui/core/DialogContent'
import DialogActions from '@material-ui/core/DialogActions'
import Button from '@material-ui/core/Button'
import useT from '../../../../i18ns/admin/useT'
import { useCommonStyles } from '../../../../utils/admin/StyleHook'
import useGDK from '../../../../providers/admin/gdk/useGDK'
import { GDKError } from '@golden/gdk-admin'
import CustomThemeType from '../../../../themes/admin/CustomThemeType'
import useGlobalDialog from '../../../../providers/admin/dialog/useGlobalDialog'
import { createGlobalDialogConfig } from '../../../../utils/default/DialogHelper'
import { useGetData, useDialogHandleSubmit, useGDKFuncHandleClick, useReload } from '../../../../utils/default/ComplexFlowHook'
import { usePageFlow } from '../../../../utils/default/PageFlowHook'
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd'
import type { DropResult } from 'react-beautiful-dnd'
import { cloneDeep } from '@golden/utils'
import ActivityWalletAddActivityTitleDialog from './ActivityWalletAddActivityTitleDialog'
import ActivityWalletUpdateActivityTitleDialog from './ActivityWalletUpdateActivityTitleDialog'
import clsx from 'clsx'

const useStyles = makeStyles((theme: CustomThemeType) => ({
  actions: {
    paddingBottom: theme.spacing(4)
  },
  header: {
    padding: '10px 16px'
  },
  item: {
    border: '1px solid #e9e9e9',
    background: '#FFF',
    '&:not(:first-child)': {
      marginTop: '-1px'
    }
  },
  text: {
    paddingRight: '70px'
  },
  update: {
    color: theme.custom.palette.blue.main,
    cursor: 'pointer'
  },
  delete: {
    color: theme.palette.error.main,
    cursor: 'pointer'
  }
}))

const ActivityItem: React.FC<{ id: number, index: number, title: string, onClose: () => void, reload: () => void }> = (props) => {
  const { id, index, title, onClose, reload } = props
  const classes = useStyles()
  const commonClasses = useCommonStyles()
  const { t } = useT()
  const globalDialog = useGlobalDialog()
  const gdk = useGDK()
  const [updateDialogOpen, setUpdateDialogOpen] = useState(false)
  const handleAutoSelect = useCallback(() => {
    setUpdateDialogOpen(false)
    onClose()
  }, [])

  const { handleSubmit: handleDeleteSubmit } = useDialogHandleSubmit({
      dialogId: `deleteActivityTitle-${id}`,
      globalDialog,
      getChangeDialogConfig: (form) => createGlobalDialogConfig({
        showIcon: false,
        message: t('dialog.deleteActivityWalletName', { name: title, count: form.batch_count, record: form.total_records })
      }),
      getSuccessDialogConfig: useCallback(() => createGlobalDialogConfig({
        showIcon: false,
        showCancel: false,
        message: t('dialog.deleteFinish')
      }), [t]),
      getFailDialogConfig: useCallback((_, error) => {
        return createGlobalDialogConfig({
          showIcon: true,
          variant: 'error',
          showCancel: false,
          message: error?.message
        })
      }, []),
      formToRequest: (form: { batch_count: number, total_records: number }) => (form),
      gdkFunc: () => gdk.activity.deleteWalletActivityTitleName(id),
      gdkFuncDependencies: [gdk],
      afterSuccessDialog: () => {
        reload()
      }
  })

  const { handleClick: handleDelete } = useGDKFuncHandleClick({
    payload: id,
    gdkFunc: (payload) => gdk.activity.getActivityWalletActivityTitleStats(payload),
    gdkFuncDependencies: [gdk],
    onSuccess: (res) => {
      handleDeleteSubmit({ batch_count: res.batch_count, total_records: res.total_records })
    },
    onError: (error) => {
      globalDialog.setConfig(createGlobalDialogConfig({
        variant: 'error',
        message: error.message,
        showIcon: false,
        showCancel: false
      }))
      globalDialog.setOpen({
        id: `updateFailed-${id}`,
        value: true,
        isOK: false
      })
    }
  })
  return (
    <>
      <ActivityWalletUpdateActivityTitleDialog id={id} open={updateDialogOpen} autoSelect={handleAutoSelect} close={() => { setUpdateDialogOpen(false) }} reload={reload} />
      <Draggable draggableId={`activity-${id}`} index={index}>
        {
          (provided) => {
            const { draggableProps, dragHandleProps, innerRef } = provided
            return (
              <ListItem
                divider
                ref={innerRef}
                className={classes.item}
                {...draggableProps}
              >
                <ListItemIcon {...dragHandleProps}>
                  <DragHandleIcon />
                </ListItemIcon>
                <ListItemText
                  className={clsx(commonClasses.pre, classes.text)}
                  primary={title}
                />
                <ListItemSecondaryAction>
                  <Box display="flex">
                    <Box marginX={1} className={classes.update} onClick={() => { setUpdateDialogOpen(true) }}>
                      {t('common.update')}
                    </Box>
                    <Box marginX={1} className={classes.delete} onClick={handleDelete}>
                      {t('common.delete')}
                    </Box>
                  </Box>
                </ListItemSecondaryAction>
              </ListItem>
            )
          }
        }
      </Draggable>
    </>
  )
}

const ActivityWalletSortActivityTitleDialog: React.FC<{ open: boolean, onClose: () => void, reloadOptions: () => void }> = (props) => {
  const { open, onClose, reloadOptions } = props
  const { t } = useT()
  const globalDialog = useGlobalDialog()
  const gdk = useGDK()
  const classes = useStyles()
  const commonClasses = useCommonStyles()
  const [order, setOrder] = useState<Array<{ id: number, title: string, order: number | null }>>([])
  const pageFlow = usePageFlow()
  const [addDialogOpen, setAddDialogOpen] = useState(false)
  const handleAutoSelect = useCallback(() => {
    setAddDialogOpen(false)
    onClose()
  }, [])
  const { reloadFlag, reload: reloadSort } = useReload()

  const reload = useCallback(() => {
    reloadSort()
    reloadOptions()
  }, [])

  useGetData({
    gdkFunc: () => gdk.activity.getActivityWalletActivityTitle(),
    gdkFuncDependencies: [gdk, open, reloadFlag],
    onBeforeFetch: pageFlow.setLoadingStart,
    onSuccess: (res) => {
      setOrder(res)
      pageFlow.setContentShow()
    },
    onError: pageFlow.setGDKError,
    canLoadData: open
  })

  const [handleSubmitDebounced] = useDebouncedCallback(useCallback(() => {
    onClose()
    gdk.activity.updateActivityWalletActivityTitleOrder({ activity_wallet_titles: order.map(item => item.id) }).subscribe({
      next: () => {
        setOrder([])
        reload()
      },
      error: (error: GDKError) => {
        setOrder([])
        reload()
        globalDialog.setConfig(createGlobalDialogConfig({
          variant: 'error',
          showIcon: true,
          showCancel: false,
          message: error.message
        }))
        globalDialog.setOpen({
          id: 'sortorderError',
          value: false,
          isOK: false
        })
      }
    })
  }, [gdk, onClose, order]), 200)
  const handleClose = useCallback(() => {
    onClose()
    setOrder([])
  }, [onClose])
  const handleSubmit = useCallback(handleSubmitDebounced, [])
  const handleSorting = (result: DropResult) => {
    const { source, destination } = result
    if (!destination) return

    const { droppableId: sourceId, index: sourceIndex } = source
    const { droppableId: targetId, index: targetIndex } = destination
    if (sourceId === targetId && sourceIndex === targetIndex) return

    const neworder = cloneDeep(order)

    const [target] = neworder.splice(sourceIndex, 1)
    neworder.splice(targetIndex, 0, target)

    setOrder(neworder)
  }

  return (
    <>
      <ActivityWalletAddActivityTitleDialog open={addDialogOpen} autoSelect={handleAutoSelect} close={() => { setAddDialogOpen(false) }} reload={reload} />
      <Dialog open={open} fullWidth maxWidth="sm">
        <Box padding={2} className={commonClasses.dialogPinkHeader}>
          {t('common.updateActivityNameList')}
        </Box>
        <DialogContent>
          <Box padding={3}>
            <Box paddingTop={1} display="flex" justifyContent="flex-end">
              <span className={commonClasses.anchor} onClick={() => { setAddDialogOpen(true) }}>
                {t('common.addActivityName')}
              </span>
            </Box>
            <DragDropContext onDragEnd={handleSorting}>
              <Droppable droppableId='droppable-1'>
                {
                  (provided) => {
                    const { droppableProps, innerRef, placeholder } = provided
                    return (
                      <List
                        disablePadding
                        ref={innerRef}
                        {...droppableProps}
                      >
                        <Box className={clsx(commonClasses.pinkTitleBar, classes.header)}>{t('common.activityName')}</Box>
                        {
                          order.map((item, index) => (<ActivityItem key={item.id} {...item} index={index} onClose={onClose} reload={reload} />))
                        }
                        {placeholder}
                      </List>
                    )
                  }
                }
              </Droppable>
            </DragDropContext>
          </Box>
        </DialogContent>
        <DialogActions className={classes.actions}>
          <Grid container direction="row" spacing={2} justifyContent="center">
            <Grid item>
              <Button
                className={commonClasses.greyButton}
                onClick={handleClose}
              >
                {t('common.cancel')}
              </Button>
            </Grid>
            <Grid item>
              <Button
                className={commonClasses.purpleGradualButton}
                onClick={handleSubmit}
              >
                {t('common.saveSetting')}
              </Button>
            </Grid>
          </Grid>
        </DialogActions>
      </Dialog>
    </>
  )
}

export default React.memo(ActivityWalletSortActivityTitleDialog)
